import React, { useState, useEffect, useRef, useCallback } from 'react';
import { copilotoService } from '../../services/api';
import authService from '../../services/authService';
import { showInfo } from '../../utils/toast';
import LoadingSpinner from '../LoadingSpinner';
import './CopilotoPanel.css';

/**
 * Copiloto (#47/TD106) — F1: painel de chat READ-ONLY sobre UMA NC.
 *
 * A IA explica e SUGERE, mas NUNCA altera a análise (a condução que constrói a
 * análise é a fase paga F2+). O gate de tier (Pro+) é autoritativo no backend
 * (403); aqui o `liberado` (de user.copiloto_liberado) decide chat × upsell —
 * o front consome o sinal, não reimplementa a regra.
 *
 * Props:
 *   ncId      — ID da NC
 *   ncLabel   — rótulo amigável (ex.: RNC-2026-0041)
 *   isOpen    — controla visibilidade do drawer
 *   onClose   — fecha o drawer
 *   liberado  — true se o plano permite o Copiloto (Pro+); false → upsell
 *   mentor    — true (flag global, só Investigação) → vira MENTOR: explica/critica
 *               a análise existente e ensina o método. NUNCA escreve a análise por
 *               você (a régua dura vive no backend; aqui é só copy/sugestões).
 */
const SUGESTOES = [
  'Essa causa raiz está robusta?',
  'Que evidência reforçaria os porquês?',
  'Há um risco que não considerei?',
];

// Sugestões do modo MENTOR: voltadas a explicar/criticar/ensinar — não a "preencher".
const SUGESTOES_MENTOR = [
  'Explique esta análise em termos simples.',
  'O que está mais fraco nesta investigação?',
  'O que a ISO 9001 exige neste ponto?',
];

// #47 streaming — id reservado da bolha do assistente enquanto ela cresce token-a-token.
// Só existe uma por vez (o envio é bloqueado por `enviando`). Trocado pela msg real no fim.
const STREAM_ID = '__streaming__';

function CopilotoPanel({ ncId, ncLabel, isOpen, onClose, liberado = true, mentor = false, streaming = false, onJumpToSection }) {
  const [mensagens, setMensagens] = useState([]);
  const [input, setInput] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [carregandoHist, setCarregandoHist] = useState(false);
  const [erro, setErro] = useState(null);
  const fimRef = useRef(null);
  const inputRef = useRef(null);

  // Carrega o histórico da sessão ativa ao abrir (só se liberado).
  useEffect(() => {
    if (!isOpen || !liberado || !ncId) return;
    let vivo = true;
    setErro(null);
    setCarregandoHist(true);
    copilotoService.listarMensagens(ncId)
      .then((res) => { if (vivo) setMensagens(res?.mensagens || []); })
      .catch((err) => {
        if (!vivo) return;
        if (err.response?.status === 401) { onClose?.(); showInfo('Sua sessão expirou — faça login novamente.'); authService.handleSessionExpired(); return; }
        // 403 não deveria ocorrer (liberado=true), mas degrada limpo.
        setErro('Não foi possível carregar a conversa. Tente reabrir o Copiloto.');
      })
      .finally(() => { if (vivo) setCarregandoHist(false); });
    return () => { vivo = false; };
  }, [isOpen, liberado, ncId, onClose]);

  // Auto-scroll pro fim a cada mensagem nova / enquanto a IA responde.
  useEffect(() => {
    if (isOpen) fimRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens, enviando, isOpen]);

  const enviar = useCallback(async (texto) => {
    const conteudo = (texto ?? input).trim();
    if (!conteudo || enviando) return;
    setErro(null);
    setEnviando(true);
    // Otimista: mostra a pergunta do analista na hora (o backend também a grava).
    setMensagens((prev) => [...prev, { id: `tmp-${prev.length}`, role: 'user', content: conteudo }]);
    setInput('');

    // Tratamento de erro compartilhado (síncrono + streaming). FastAPI 422 devolve
    // `detail` como ARRAY — nunca renderizar objeto como filho React. Streaming
    // expõe `err.streamDetail` (frame 'error') além de `err.response`.
    const tratarErro = (err) => {
      const status = err.response?.status;
      const raw = err.response?.data?.detail;
      const detail = typeof raw === 'string' ? raw
        : (typeof err.streamDetail === 'string' ? err.streamDetail : null);
      if (status === 401) { onClose?.(); showInfo('Sua sessão expirou — faça login novamente.'); authService.handleSessionExpired(); return; }
      if (status === 429) setErro(detail || 'Limite de mensagens desta conversa atingido.');
      else if (status === 403) setErro('O Copiloto está disponível a partir do plano Pro.');
      else if (status === 503) setErro('O copiloto está indisponível no momento. Sua mensagem foi salva — tente de novo em instantes.');
      else setErro(detail || 'Não foi possível enviar. Tente novamente.');
    };

    try {
      if (streaming) {
        // SSE: a bolha do assistente cresce token-a-token (resolve a lentidão percebida).
        const done = await copilotoService.enviarMensagemStream(ncId, conteudo, {
          onChunk: (delta) => setMensagens((prev) => {
            if (prev.some((m) => m.id === STREAM_ID)) {
              return prev.map((m) => (m.id === STREAM_ID ? { ...m, content: m.content + delta } : m));
            }
            return [...prev, { id: STREAM_ID, role: 'assistant', content: delta }];
          }),
        });
        // finaliza: troca a bolha de streaming pela mensagem real (id estável p/ o
        // reload de histórico). Se não persistiu, mantém o texto com um id local.
        setMensagens((prev) => prev.map((m) => {
          if (m.id !== STREAM_ID) return m;
          return done?.mensagem ? done.mensagem : { ...m, id: `local-${prev.length}` };
        }));
      } else {
        const res = await copilotoService.enviarMensagem(ncId, conteudo);
        if (res?.mensagem) setMensagens((prev) => [...prev, res.mensagem]);
      }
    } catch (err) {
      // tira a bolha de streaming parcial/vazia antes de exibir o erro
      setMensagens((prev) => prev.filter((m) => m.id !== STREAM_ID));
      tratarErro(err);
    } finally {
      setEnviando(false);
      inputRef.current?.focus();
    }
  }, [input, enviando, ncId, onClose, streaming]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); }
  };

  if (!isOpen) return null;

  // Copy/sugestões dependem do modo. Mentor = tutor/crítico da análise existente.
  const titulo = mentor ? 'Mentor' : 'Copiloto';
  const iconeHeader = mentor ? 'school' : 'forum';
  const sugestoes = mentor ? SUGESTOES_MENTOR : SUGESTOES;

  return (
    <div className="copiloto-overlay" onClick={onClose}>
      <aside
        className="copiloto-panel"
        role="dialog"
        aria-modal="true"
        aria-label={`${titulo} da NC`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="copiloto-header">
          <div className="copiloto-header-title">
            <span className="material-symbols-outlined">{iconeHeader}</span>
            <div>
              <strong>{titulo}</strong>
              <span className="copiloto-header-sub">{ncLabel}</span>
            </div>
          </div>
          <button className="copiloto-close" onClick={onClose} aria-label="Fechar">
            <span className="material-symbols-outlined">close</span>
          </button>
        </header>

        {!liberado ? (
          <div className="copiloto-upsell">
            <span className="material-symbols-outlined copiloto-upsell-icon">workspace_premium</span>
            <h4>{mentor ? 'Tenha um mentor de qualidade ao lado' : 'Converse com o Copiloto da sua qualidade'}</h4>
            <p>
              {mentor ? (
                <>Peça ao Mentor para <strong>explicar e criticar</strong> esta investigação e
                  ensinar o método (ISO 9001, 5 porquês, Ishikawa). Disponível <strong>a partir do plano Pro</strong>.</>
              ) : (
                <>Tire dúvidas e peça sugestões sobre esta Não Conformidade — uma causa
                  mais profunda, uma ação mais específica, um risco que passou batido.
                  O Copiloto está disponível <strong>a partir do plano Pro</strong>.</>
              )}
            </p>
            <p className="copiloto-upsell-fine">
              {mentor ? 'Ele explica, critica e ensina; quem escreve a análise é você.' : 'Ele explica e sugere; você decide o que aplicar.'}
            </p>
          </div>
        ) : (
          <>
            <div className="copiloto-disclaimer">
              {mentor ? (
                <>O Mentor <strong>explica e critica</strong> esta investigação e ensina o método —
                  ele não escreve a análise por você. Você corrige; ele orienta.</>
              ) : (
                <>O Copiloto <strong>explica e sugere</strong> sobre esta NC — ele não altera a análise.
                  Você decide o que aplicar.</>
              )}
            </div>

            {/* Ponte Fase 1 (#47) — barra de navegação rápida pro editor. O Mentor APONTA;
                aqui você pula direto pra seção pra CORRIGIR à mão (mentor read-only intacto,
                ninguém escreve por você). Só no modo mentor + quando o caller liga a ponte. */}
            {mentor && onJumpToSection && (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', padding: '8px 12px', borderBottom: '1px solid #e5e7eb' }}>
                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>Ir para a seção:</span>
                {[
                  ['Contexto', 'editor-secao-contexto'],
                  ['6M', 'editor-secao-6m'],
                  ['Porquês', 'editor-secao-porques'],
                  ['Causa', 'editor-secao-causa'],
                  ['Ações', 'editor-secao-acoes'],
                  ['Eficácia', 'editor-secao-eficacia'],
                ].map(([rotulo, secaoId]) => (
                  <button
                    key={secaoId}
                    type="button"
                    onClick={() => onJumpToSection(secaoId)}
                    style={{ padding: '3px 10px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '999px', background: '#f8fafc', color: '#334155', cursor: 'pointer' }}
                  >
                    {rotulo}
                  </button>
                ))}
              </div>
            )}

            <div className="copiloto-mensagens">
              {carregandoHist ? (
                <div className="copiloto-loading-hist"><LoadingSpinner size="small" /> <span>Carregando conversa…</span></div>
              ) : mensagens.length === 0 ? (
                <div className="copiloto-empty">
                  <span className="material-symbols-outlined">tips_and_updates</span>
                  <p>{mentor ? 'Peça uma explicação ou uma crítica desta análise. Por exemplo:' : 'Pergunte qualquer coisa sobre esta NC. Por exemplo:'}</p>
                  <div className="copiloto-sugestoes">
                    {sugestoes.map((s) => (
                      <button key={s} className="copiloto-chip" onClick={() => enviar(s)} disabled={enviando}>{s}</button>
                    ))}
                  </div>
                </div>
              ) : (
                mensagens.map((m) => (
                  <div key={m.id} className={`copiloto-msg copiloto-msg-${m.role === 'user' ? 'user' : 'assistant'}`}>
                    {m.content}
                  </div>
                ))
              )}
              {/* "pensando…" some assim que a bolha de streaming começa a crescer
                  (no streaming, o próprio texto crescendo já é o indicador). */}
              {enviando && !mensagens.some((m) => m.id === STREAM_ID) && (
                <div className="copiloto-msg copiloto-msg-assistant copiloto-msg-typing">
                  <LoadingSpinner size="small" /> <span>{titulo} está pensando…</span>
                </div>
              )}
              <div ref={fimRef} />
            </div>

            {erro && <div className="copiloto-erro" role="alert">{erro}</div>}

            <div className="copiloto-input-row">
              <textarea
                ref={inputRef}
                className="copiloto-input"
                rows={2}
                maxLength={4000}
                value={input}
                placeholder="Escreva sua pergunta… (Enter envia, Shift+Enter quebra linha)"
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                disabled={enviando}
              />
              <button
                className="copiloto-send"
                onClick={() => enviar()}
                disabled={enviando || !input.trim()}
                aria-label="Enviar"
                title="Enviar"
              >
                <span className="material-symbols-outlined">send</span>
              </button>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

export default CopilotoPanel;
